import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export type SortDirection = 'asc' | 'desc'
export type SortValue = string | number | boolean | Date | null | undefined
export function useSortedRows<T, C extends Record<string, (row: T) => SortValue>>(
  rows: readonly T[], columns: C, defaultKey: keyof C & string,
  defaultDirection: SortDirection = 'asc', locale?: string,
  onSortChange?: (key: keyof C & string, direction: SortDirection) => void,
  controlledKey?: keyof C & string, controlledDirection?: SortDirection,
) {
  const [sort, setSort] = useState<{key: keyof C & string; direction: SortDirection}>({
    key: defaultKey, direction: defaultDirection,
  })
  const activeKey = controlledKey ?? sort.key
  const activeDirection = controlledDirection ?? sort.direction
  const collator = useMemo(() => new Intl.Collator(locale, {
    numeric: true, sensitivity: 'base',
  }), [locale])
  const sortedRows = useMemo(() => [...rows].sort((left, right) => {
    const a = columns[activeKey](left), b = columns[activeKey](right)
    const result = compare(a, b, collator)
    return activeDirection === 'asc' ? result : -result
  }), [rows, columns, activeKey, activeDirection, collator])
  const toggle = (key: keyof C & string) => {
    const direction = activeKey === key && activeDirection === 'asc' ? 'desc' : 'asc'
    onSortChange?.(key, direction)
    setSort({ key, direction })
  }
  return { sortedRows, sortKey: activeKey, sortDirection: activeDirection, toggle }
}

export function useUrlSortedRows<T, C extends Record<string, (row: T) => SortValue>>(
  rows: readonly T[], columns: C, defaultKey: keyof C & string,
  defaultDirection: SortDirection = 'asc', locale?: string,
) {
  const [params, setParams] = useSearchParams()
  const requestedKey = params.get('sort')
  const controlledKey = requestedKey && Object.hasOwn(columns, requestedKey)
    ? requestedKey as keyof C & string : defaultKey
  const requestedDirection = params.get('direction')
  const controlledDirection: SortDirection = requestedDirection === 'desc' ? 'desc'
    : requestedDirection === 'asc' ? 'asc' : defaultDirection
  return useSortedRows(rows, columns, defaultKey, defaultDirection, locale, (key, direction) => {
    const next = new URLSearchParams(params)
    next.set('sort', key)
    next.set('direction', direction)
    next.set('page', '1')
    next.delete('edit')
    next.delete('new')
    setParams(next)
  }, controlledKey, controlledDirection)
}

export function useUrlServerSortedRows<T, C extends Record<string, (row: T) => SortValue>>(
  rows: readonly T[], columns: C, defaultKey: keyof C & string,
  defaultDirection: SortDirection = 'asc',
) {
  const [params, setParams] = useSearchParams()
  const requestedKey = params.get('sort')
  const sortKey = requestedKey && Object.hasOwn(columns, requestedKey)
    ? requestedKey as keyof C & string : defaultKey
  const requestedDirection = params.get('direction')
  const sortDirection: SortDirection = requestedDirection === 'desc' ? 'desc'
    : requestedDirection === 'asc' ? 'asc' : defaultDirection
  const toggle = (key: keyof C & string) => {
    const direction = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc'
    const next = new URLSearchParams(params)
    next.set('sort', key)
    next.set('direction', direction)
    next.set('page', '1')
    next.delete('edit')
    next.delete('new')
    setParams(next)
  }
  return { sortedRows: rows, sortKey, sortDirection, toggle }
}

export function listParams(current: URLSearchParams, values: Record<string, string | undefined>) {
  const next = new URLSearchParams()
  const sort = current.get('sort'), direction = current.get('direction')
  if (sort) next.set('sort', sort)
  if (direction === 'asc' || direction === 'desc') next.set('direction', direction)
  for (const [key, value] of Object.entries(values)) if (value) next.set(key, value)
  return next
}

function compare(a: SortValue, b: SortValue, collator: Intl.Collator) {
  if (a == null) return b == null ? 0 : 1
  if (b == null) return -1
  const left = a instanceof Date ? a.getTime() : a
  const right = b instanceof Date ? b.getTime() : b
  if (typeof left === 'number' && typeof right === 'number') return left - right
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right)
  return collator.compare(String(left), String(right))
}

export function SortableTableHeader<K extends string>({sortKey, activeKey,
  direction, label, onSort}:{sortKey:K;activeKey:K;direction:SortDirection;
  label:string;onSort:(key:K)=>void}) {
  const active = sortKey === activeKey
  return <th aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
    <button type="button" className="table-sort-button" onClick={() => onSort(sortKey)}>
      <span>{label}</span><span className="table-sort-indicator" aria-hidden="true">
        {active ? (direction === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  </th>
}

export function SortableHeaders<K extends string>({sort, columns}:{
  sort:{sortKey:K;sortDirection:SortDirection;toggle:(key:K)=>void};
  columns:ReadonlyArray<readonly [K,string]>;
}) {
  return <>{columns.map(([key,label]) => <SortableTableHeader key={key}
    sortKey={key} activeKey={sort.sortKey} direction={sort.sortDirection}
    label={label} onSort={sort.toggle}/>)}</>
}
